import React, { useState, useEffect } from 'react';
import { Typography, Form, Input, Switch, Card, Button, Alert, Spin, Divider, message } from 'antd';
import { getSettings, updateSetting } from '../services/settingsService';

const { Title } = Typography;
const { TextArea } = Input;

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await getSettings();
      const formValues = {};

      data.forEach(setting => {
        formValues[setting.key] = setting.value;
      });

      form.setFieldsValue({
        crm_enabled: formValues.crm_enabled || false,
        contacts_title: formValues.contacts_title || 'Contacts',
        projects_title: formValues.projects_title || 'Projects',
        project_types: formValues.project_types?.join(', ') || '',
        project_statuses: formValues.project_statuses?.join(', ') || '',
        project_categories: formValues.project_categories?.join(', ') || '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async values => {
    try {
      setLoading(true);

      // Update each setting individually
      await Promise.all([
        updateSetting('crm_enabled', values.crm_enabled),
        updateSetting('contacts_title', values.contacts_title),
        updateSetting('projects_title', values.projects_title),
        updateSetting(
          'project_types',
          values.project_types.split(',').map(s => s.trim())
        ),
        updateSetting(
          'project_statuses',
          values.project_statuses.split(',').map(s => s.trim())
        ),
        updateSetting(
          'project_categories',
          values.project_categories.split(',').map(s => s.trim())
        ),
      ]);

      message.success('Settings updated successfully');
    } catch (err) {
      setError(err.message);
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Settings</Title>

      <Card style={{ maxWidth: 800, margin: '24px auto' }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Divider orientation="left">CRM Settings</Divider>

          <Form.Item name="crm_enabled" label="Enable CRM" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="contacts_title"
            label="Contacts Title"
            rules={[{ required: true, message: 'Please enter contacts title' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="projects_title"
            label="Projects Title"
            rules={[{ required: true, message: 'Please enter projects title' }]}
          >
            <Input />
          </Form.Item>

          <Divider orientation="left">Project Settings</Divider>

          <Form.Item
            name="project_types"
            label="Project Types (comma-separated)"
            rules={[{ required: true, message: 'Please enter project types' }]}
          >
            <TextArea rows={2} placeholder="event, campaign, development, consulting, other" />
          </Form.Item>

          <Form.Item
            name="project_statuses"
            label="Project Statuses (comma-separated)"
            rules={[{ required: true, message: 'Please enter project statuses' }]}
          >
            <TextArea
              rows={2}
              placeholder="draft, proposed, active, on_hold, completed, cancelled"
            />
          </Form.Item>

          <Form.Item
            name="project_categories"
            label="Project Categories (comma-separated)"
            rules={[{ required: true, message: 'Please enter project categories' }]}
          >
            <TextArea rows={2} placeholder="marketing, development, design, consulting" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default SettingsPage;
